/**
 * Features that are hidden in every deployed environment (dev, staging, prod)
 * but remain visible on the local Vite dev server so they can keep being developed.
 *
 * On localhost, Settings → Localhost preview can hide these so the shell matches
 * Dev/Prod. That toggle is stored in localStorage and applied at module load
 * (the page reloads after a change).
 *
 * To re-enable a feature everywhere, delete its entry here and remove the
 * `FEATURE_VISIBILITY.<key>` checks at the call sites.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

const UNIFY_ADMIN_SHELL_ROLES = new Set([
  'Unify Admin',
  'unify-admin',
  'unify-assistant',
]);

export function isLocalDevHost(hostname: string = globalThis.location?.hostname ?? ''): boolean {
  return LOCAL_HOSTNAMES.has(hostname);
}

export const SHOW_LOCALHOST_ONLY_UI_KEY = 'unify.showLocalhostOnlyUi';

/** Loopback host + Vite `npm run dev`. True even when localhost-only UI is toggled off. */
export function isLocalhostDevShell(
  hostname: string = globalThis.location?.hostname ?? '',
  isViteDev: boolean = import.meta.env.DEV,
): boolean {
  return isLocalDevHost(hostname) && isViteDev;
}

/** Persist the Settings toggle. Missing / invalid values default to showing localhost-only UI. */
export function readShowLocalhostOnlyUi(): boolean {
  try {
    const raw = globalThis.localStorage?.getItem(SHOW_LOCALHOST_ONLY_UI_KEY);
    if (raw === '0' || raw === 'false') return false;
    return true;
  } catch {
    return true;
  }
}

export function writeShowLocalhostOnlyUi(show: boolean): void {
  try {
    globalThis.localStorage.setItem(SHOW_LOCALHOST_ONLY_UI_KEY, show ? '1' : '0');
  } catch {
    // Ignore quota / missing localStorage (tests, private mode).
  }
}

/**
 * Localhost-only UI is shown only on a loopback host running the Vite dev server,
 * and only while the Settings toggle is on (default on).
 * Hosted Dev/staging/prod are `vite build` (`import.meta.env.DEV === false`), so they stay hidden
 * even if someone port-forwards the deployed app to localhost.
 */
export function isLocalhostOnlyVisible(
  hostname: string = globalThis.location?.hostname ?? '',
  isViteDev: boolean = import.meta.env.DEV,
  showLocalhostOnlyUi: boolean = true,
): boolean {
  return isLocalhostDevShell(hostname, isViteDev) && showLocalhostOnlyUi;
}

/** Unify Admin / Unify Assistant display shell (assistants share the Unify Admin nav). */
export function isUnifyAdminShellRole(role: string | undefined): boolean {
  return role != null && UNIFY_ADMIN_SHELL_ROLES.has(role);
}

const localOnly = isLocalhostOnlyVisible(
  globalThis.location?.hostname ?? '',
  import.meta.env.DEV,
  readShowLocalhostOnlyUi(),
);

export const FEATURE_VISIBILITY = {
  /** Header "+" quick-add menu. Hidden everywhere until the flow is ready. */
  headerQuickAdd: false,
  /** Referral Requests nav item, route, and Caseload tab. */
  referralRequests: localOnly,
  /** Caseload "All" tab (Clients is the primary tab). */
  caseloadAllTab: localOnly,
  /** User profile → Locations tab. */
  profileLocationsTab: localOnly,
  /** User profile → Permissions tab resource/UI permission reference. */
  profileRolePermissions: localOnly,
  /** Settings → Billing & Finances (placeholder staff billing page). */
  settingsBilling: localOnly,
  /** Client chart → Medications panel. */
  chartMedications: localOnly,
  /** Client portal dashboard → Pending Service Requests. */
  clientPortalPendingServiceRequests: localOnly,
  /** Client portal Documents nav, route, and page. */
  clientPortalDocuments: localOnly,
  /** Client portal Messages nav, route, and page. */
  clientPortalMessages: localOnly,
  /** Client portal Billing ledger tabs (invoices, services, statements, payments). */
  clientPortalBillingLedger: localOnly,
  /** Client portal Payment Methods → insurance plans (credit cards stay). */
  clientPortalPaymentMethodsInsurance: localOnly,
  /** Settings → Notifications. */
  settingsNotifications: localOnly,
  /** Provider dashboard → Practice Resources. */
  dashboardPracticeResources: localOnly,
  /** Caseload → Transfer Portal. */
  caseloadTransferPortal: localOnly,
  /** Client chart Overview → Emergency Contacts. */
  chartEmergencyContacts: localOnly,
  /** Client chart Medical Record → Contacts panel. */
  chartContacts: localOnly,
  /** Client chart Measures → Health Metrics. */
  chartHealthMetrics: localOnly,
  /** Staff Messages nav, route, and chart Message CTA. */
  staffMessages: localOnly,
  /** Tasks extras (create, bulk edit) until the Tasks PRD. */
  tasksAdvanced: localOnly,
  /** Trainings nav, route, and page. */
  trainings: localOnly,
  /** Provider Enrollments nav and route. */
  enrollments: localOnly,
  /** Settings → Profile → Clinical Info (providers only; hidden for Unify Admin editors). */
  settingsClinicalInfo: localOnly,
  /** Settings → Profile → Appointment Types (providers only; hidden for Unify Admin editors). */
  settingsAppointmentTypes: localOnly,
  /** Supervision → Compliance tab. */
  supervisionCompliance: localOnly,
  /** Analytics nav and `/dashboard/analytics/*`. */
  analytics: localOnly,
  /** Practice/Unify Finances nav and `/dashboard/finances` / `/dashboard/practice-finances`. */
  finances: localOnly,
  /** Practice Enrollments nav and `/dashboard/practices/enrollments`. */
  practiceEnrollments: localOnly,
  /**
   * Service Requests nav and `/dashboard/episode-tracker`.
   * Unify Admin / Assistant still see this in hosted envs.
   */
  serviceRequests: localOnly,
  /** Staff Billing nav and `/dashboard/billing/*`. */
  staffBilling: localOnly,
  /** Groups nav and `/dashboard/groups`. */
  groups: localOnly,
  /** Settings → EOC Types (Unify Admin keeps this in hosted envs). */
  settingsEocTypes: localOnly,
  /** Settings → Payers (Unify Admin keeps this in hosted envs). */
  settingsPayers: localOnly,
  /** Settings → Billing entities (Unify Admin slug only; not Unify Admin shell). */
  settingsBillingEntities: localOnly,
  /** Settings → Documentation library (Unify Admin keeps this in hosted envs). */
  settingsDocumentation: localOnly,
  /** Documentation → Template settings tab. */
  settingsTemplateSettings: localOnly,
  /** Settings → Reports & Analytics. */
  settingsReports: localOnly,
  /** IT settings → Styling Playground. */
  settingsStylingPlayground: localOnly,
  /** Practice settings → Users tab. */
  practiceSettingsUsers: localOnly,
  /** Practice settings → Legal tab. */
  practiceSettingsLegal: localOnly,
  /** Practice settings → Branding tab. */
  practiceSettingsBranding: localOnly,
  /** Practice settings → Portal tab. */
  practiceSettingsPortal: localOnly,
  /** Practice settings → Finances tab. */
  practiceSettingsFinances: localOnly,
} as const;

export type FeatureVisibilityKey = keyof typeof FEATURE_VISIBILITY;

/** Client-portal paths that are FEATURE_VISIBILITY-gated independently of RBAC. */
export function isClientPortalFeatureVisible(pathname: string): boolean {
  if (pathname.startsWith('/client-portal/documents')) {
    return FEATURE_VISIBILITY.clientPortalDocuments;
  }
  if (pathname.startsWith('/client-portal/messages')) {
    return FEATURE_VISIBILITY.clientPortalMessages;
  }
  if (
    pathname.startsWith('/client-portal/request-service')
    || pathname.startsWith('/client-portal/requests')
  ) {
    return FEATURE_VISIBILITY.clientPortalPendingServiceRequests;
  }
  return true;
}

function unifyAdminKeepsPath(pathname: string, role: string | undefined): boolean {
  if (!isUnifyAdminShellRole(role)) return false;
  return (
    pathname.startsWith('/dashboard/episode-tracker')
    || pathname.startsWith('/dashboard/settings/sessions')
    || pathname.startsWith('/dashboard/settings/payers')
    || pathname.startsWith('/dashboard/settings/documentation')
  );
}

/** Clinical settings is the Unify Admin role only, not the admin shell (that includes unify-assistant). */
export function isClinicalSettingsVisible(role?: string, roleSlug?: string): boolean {
  if (roleSlug) return roleSlug === 'unify-admin';
  return role === 'Unify Admin' || role === 'unify-admin';
}

/** Staff `/dashboard/*` paths gated independently of RBAC. */
export function isStaffFeaturePathVisible(pathname: string, role?: string, roleSlug?: string): boolean {
  if (pathname.startsWith('/dashboard/settings/billing-entities')) {
    return roleSlug === 'unify-admin';
  }
  if (pathname.startsWith('/dashboard/settings/clinical')) {
    return isClinicalSettingsVisible(role, roleSlug);
  }
  if (unifyAdminKeepsPath(pathname, role)) return true;
  if (pathname.startsWith('/dashboard/messages')) return FEATURE_VISIBILITY.staffMessages;
  if (pathname.startsWith('/dashboard/trainings')) return FEATURE_VISIBILITY.trainings;
  if (pathname.startsWith('/dashboard/practices/enrollments')) return FEATURE_VISIBILITY.practiceEnrollments;
  if (pathname.startsWith('/dashboard/enrollments')) return FEATURE_VISIBILITY.enrollments;
  if (pathname.startsWith('/dashboard/analytics')) return FEATURE_VISIBILITY.analytics;
  if (pathname.startsWith('/dashboard/finances') || pathname.startsWith('/dashboard/practice-finances')) {
    return FEATURE_VISIBILITY.finances;
  }
  if (pathname.startsWith('/dashboard/episode-tracker')) return FEATURE_VISIBILITY.serviceRequests;
  if (pathname.startsWith('/dashboard/billing')) return FEATURE_VISIBILITY.staffBilling;
  if (pathname.startsWith('/dashboard/groups')) return FEATURE_VISIBILITY.groups;
  if (pathname.startsWith('/dashboard/referral-requests')) return FEATURE_VISIBILITY.referralRequests;
  if (pathname.startsWith('/dashboard/supervision/compliance')) return FEATURE_VISIBILITY.supervisionCompliance;
  if (pathname.startsWith('/dashboard/styling')) return FEATURE_VISIBILITY.settingsStylingPlayground;
  if (pathname.startsWith('/dashboard/settings/notifications')) return FEATURE_VISIBILITY.settingsNotifications;
  if (pathname.startsWith('/dashboard/settings/billing')) return FEATURE_VISIBILITY.settingsBilling;
  if (pathname.startsWith('/dashboard/settings/sessions')) return FEATURE_VISIBILITY.settingsEocTypes;
  if (pathname.startsWith('/dashboard/settings/payers')) return FEATURE_VISIBILITY.settingsPayers;
  if (pathname.startsWith('/dashboard/settings/documentation')) return FEATURE_VISIBILITY.settingsDocumentation;
  if (pathname.startsWith('/dashboard/settings/reports')) return FEATURE_VISIBILITY.settingsReports;
  return true;
}
